import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Flame, TrendingUp, BadgeCheck, Share2, ArrowRight, Sparkles, Copy } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';
import SEO from '../components/SEO';

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Public shareable "Top de la semana" page at /c/top.
 * Designed to look great in screenshots/Instagram stories — branded layout,
 * the two ranking cards centered, watermark with bravoproductora.com.
 * Includes Web Share API trigger with clipboard fallback.
 */
const TopOfTheWeekPage = () => {
  const [topProducer, setTopProducer] = useState(null);
  const [topDj, setTopDj] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [pRes, dRes] = await Promise.all([
          api.get('/public/producers').catch(() => ({ data: [] })),
          api.get('/public/djs').catch(() => ({ data: [] })),
        ]);
        const producers = Array.isArray(pRes.data) ? pRes.data : [];
        const djs = Array.isArray(dRes.data) ? dRes.data : [];
        setTopProducer(producers.find(p => p.upcoming_events_count > 0) || producers[0] || null);
        setTopDj(djs.find(d => d.profile_views > 0) || djs[0] || null);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const shareUrl = (() => {
    if (typeof window === 'undefined') return '';
    // Use the server-rendered share endpoint so WhatsApp/Instagram/X see the dynamic OG image
    return `${BACKEND}/api/s/top`;
  })();

  const handleShare = async () => {
    const data = {
      title: 'Top de la semana — Bravo',
      text: '🔥 Top de la semana en Bravo: las productoras y DJs más activas. Mirá quiénes están en el #1.',
      url: shareUrl,
    };
    try {
      if (typeof navigator !== 'undefined' && navigator.share) {
        await navigator.share(data);
      } else if (typeof navigator !== 'undefined' && navigator.clipboard) {
        await navigator.clipboard.writeText(shareUrl);
        toast.success('Link copiado al portapapeles');
      } else {
        window.prompt('Copiá el link:', shareUrl);
      }
    } catch (_) { /* user cancelled */ }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success('Link copiado');
    } catch (_) {
      toast.error('No se pudo copiar');
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] relative overflow-hidden">
      <SEO
        title="Top de la semana"
        description="Mirá quiénes son la #1 Productora y el #1 DJ de la semana en Bravo."
        image={`${BACKEND}/api/og/top.png`}
        url="/c/top"
      />
      {/* Animated background blobs */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <motion.div
          animate={{
            x: [0, 60, 0],
            y: [0, -40, 0],
            scale: [1, 1.1, 1],
          }}
          transition={{ duration: 14, repeat: Infinity, ease: 'easeInOut' }}
          className="absolute w-[500px] h-[500px] rounded-full blur-3xl opacity-20"
          style={{
            background: 'radial-gradient(circle, #D4AF37 0%, transparent 60%)',
            top: '-10%',
            left: '-10%',
          }}
        />
        <motion.div
          animate={{
            x: [0, -50, 0],
            y: [0, 50, 0],
            scale: [1, 1.05, 1],
          }}
          transition={{ duration: 16, repeat: Infinity, ease: 'easeInOut', delay: 2 }}
          className="absolute w-[450px] h-[450px] rounded-full blur-3xl opacity-15"
          style={{
            background: 'radial-gradient(circle, #C026D3 0%, transparent 60%)',
            bottom: '-5%',
            right: '-10%',
          }}
        />
      </div>

      {/* Grid pattern overlay */}
      <div className="absolute inset-0 pointer-events-none opacity-[0.06]">
        <svg width="100%" height="100%">
          <pattern id="top-grid" width="48" height="48" patternUnits="userSpaceOnUse">
            <path d="M 48 0 L 0 0 0 48" fill="none" stroke="#D4AF37" strokeWidth="0.5" />
          </pattern>
          <rect width="100%" height="100%" fill="url(#top-grid)" />
        </svg>
      </div>

      <div className="relative z-10 min-h-screen flex flex-col items-center justify-between px-5 py-10 sm:py-14">
        {/* Top brand */}
        <Link to="/" className="inline-flex items-center gap-2 text-white/40 hover:text-white transition-colors text-xs uppercase tracking-[0.3em]">
          <Sparkles size={12} className="text-[#D4AF37]" />
          Bravo Comunidad
        </Link>

        {/* Center hero */}
        <div className="w-full max-w-2xl">
          {/* Title */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center mb-7 sm:mb-10"
          >
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/30 mb-4">
              <Flame size={12} className="text-[#D4AF37]" />
              <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#D4AF37]">
                Top de la semana
              </span>
            </div>
            <h1
              className="text-5xl sm:text-7xl font-black uppercase tracking-tighter leading-none mb-3"
              style={{
                background: 'linear-gradient(180deg, #FFFFFF 0%, #D4AF37 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              ¿Quién manda<br />esta semana?
            </h1>
            <p className="text-white/50 text-sm sm:text-base max-w-md mx-auto">
              Las productoras más activas y los DJs trending de la plataforma.
            </p>
          </motion.div>

          {/* Cards */}
          {loading ? (
            <div className="grid grid-cols-2 gap-4">
              <div className="aspect-[3/4] bg-white/5 rounded-2xl animate-pulse" />
              <div className="aspect-[3/4] bg-white/5 rounded-2xl animate-pulse" />
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3 sm:gap-5">
              {/* Productor */}
              {topProducer && (
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                >
                  <Link
                    to={`/productora/${topProducer.slug || topProducer.id}`}
                    data-testid="top-week-producer-card"
                    className="block relative rounded-3xl overflow-hidden group border-2 border-[#D4AF37]/40 hover:border-[#D4AF37] transition-all bg-zinc-900"
                    style={{ boxShadow: '0 20px 60px -20px rgba(212,175,55,0.4)' }}
                  >
                    <div className="aspect-[3/4] relative">
                      {(topProducer.cover || topProducer.logo) ? (
                        <img
                          src={topProducer.cover || topProducer.logo}
                          alt={topProducer.name}
                          className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                        />
                      ) : (
                        <div
                          className="absolute inset-0"
                          style={{ background: `linear-gradient(135deg, ${topProducer.color || '#D4AF37'}66, #000)` }}
                        />
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-transparent" />

                      {/* Top badge */}
                      <div className="absolute top-3 left-3">
                        <div className="px-2.5 py-1 rounded-full bg-[#D4AF37] text-black text-[10px] font-black uppercase tracking-wider shadow-lg">
                          #1 Productora
                        </div>
                      </div>

                      {/* Bottom info */}
                      <div className="absolute bottom-0 left-0 right-0 p-3 sm:p-4">
                        <div className="flex items-center gap-1.5 mb-1.5">
                          <h3 className="text-white font-black text-base sm:text-xl tracking-tight truncate">
                            {topProducer.name}
                          </h3>
                          {topProducer.is_verified && (
                            <BadgeCheck size={16} className="text-blue-400 flex-shrink-0" />
                          )}
                        </div>
                        {topProducer.upcoming_events_count > 0 && (
                          <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/20 border border-emerald-500/50">
                            <Flame size={10} className="text-emerald-300" />
                            <span className="text-[10px] sm:text-xs font-bold text-emerald-300 uppercase tracking-wider">
                              {topProducer.upcoming_events_count} eventos
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </Link>
                </motion.div>
              )}

              {/* DJ */}
              {topDj && (
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.3 }}
                >
                  <Link
                    to={`/dj/${topDj.slug || topDj.id}`}
                    data-testid="top-week-dj-card"
                    className="block relative rounded-3xl overflow-hidden group border-2 border-fuchsia-500/40 hover:border-fuchsia-500 transition-all bg-zinc-900"
                    style={{ boxShadow: '0 20px 60px -20px rgba(192,38,211,0.4)' }}
                  >
                    <div className="aspect-[3/4] relative">
                      {(topDj.banner || topDj.photo) ? (
                        <img
                          src={topDj.banner || topDj.photo}
                          alt={topDj.name}
                          className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                        />
                      ) : (
                        <div className="absolute inset-0 bg-gradient-to-br from-fuchsia-600/40 to-zinc-900" />
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-transparent" />

                      <div className="absolute top-3 left-3">
                        <div className="px-2.5 py-1 rounded-full bg-fuchsia-500 text-white text-[10px] font-black uppercase tracking-wider shadow-lg">
                          #1 DJ
                        </div>
                      </div>

                      <div className="absolute bottom-0 left-0 right-0 p-3 sm:p-4">
                        <h3 className="text-white font-black text-base sm:text-xl tracking-tight truncate mb-1">
                          {topDj.name}
                        </h3>
                        {topDj.genres?.length > 0 && (
                          <p className="text-white/70 text-[10px] sm:text-xs mb-1.5 truncate">
                            {topDj.genres.join(' · ')}
                          </p>
                        )}
                        {topDj.profile_views > 0 && (
                          <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-fuchsia-500/20 border border-fuchsia-500/50">
                            <TrendingUp size={10} className="text-fuchsia-300" />
                            <span className="text-[10px] sm:text-xs font-bold text-fuchsia-300 uppercase tracking-wider">
                              {topDj.profile_views} visitas
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </Link>
                </motion.div>
              )}
            </div>
          )}

          {/* Actions */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.5 }}
            className="mt-8 sm:mt-12 flex flex-col sm:flex-row items-center justify-center gap-3"
          >
            <button
              onClick={handleShare}
              data-testid="top-week-share-btn"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-[#D4AF37] hover:bg-[#B8862E] text-black font-black uppercase tracking-widest text-xs transition-all rounded-full shadow-lg shadow-[#D4AF37]/30"
            >
              <Share2 size={14} />
              Compartir esta tarjeta
            </button>
            <button
              onClick={handleCopy}
              data-testid="top-week-copy-btn"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-5 py-3 bg-white/5 hover:bg-white/10 border border-white/15 text-white/80 font-bold uppercase tracking-widest text-xs transition-all rounded-full"
            >
              <Copy size={12} />
              Copiar link
            </button>
            <Link
              to="/eventos"
              data-testid="top-week-see-events"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-5 py-3 text-white/50 hover:text-white font-bold uppercase tracking-widest text-xs transition-all"
            >
              Ver todos los eventos
              <ArrowRight size={12} />
            </Link>
          </motion.div>
        </div>

        {/* Watermark */}
        <Link
          to="/"
          className="mt-8 inline-flex items-center gap-2 text-white/30 hover:text-white/70 text-[11px] uppercase tracking-[0.3em] transition-colors"
        >
          bravoproductora.com
        </Link>
      </div>
    </div>
  );
};

export default TopOfTheWeekPage;
